import React from 'react';
import styled from 'styled-components/macro';
import { space, layout, flexbox, borderRadius } from 'styled-system';
import { CardElement } from '@stripe/react-stripe-js';
import { Label } from './Text';
import { hexToRgb } from '../Utils/utils';

export const StyledInput = styled.input`
  font-family: ${(props) => props.theme.fontInput};
  font-size: 1.4rem;
  font-weight: normal;
  line-height: 35px;
  color: ${(props) => props.theme.inputTextColor};
  background-color: ${(props) => props.theme.inputBackgroundColor};
  border: ${(props) =>
    props.type === 'checkbox' || props.type === 'radio'
      ? `1px solid ${props.theme.inputBorderColor}`
      : 'none'};
  border-radius: ${(props) =>
    props.type === 'checkbox' || props.type === 'radio'
      ? props.theme.inputs.checkboxBorderRadius
      : props.theme.inputs.borderRadius};
  height: ${(props) =>
    props.type === 'checkbox' || props.type === 'radio' ? '17px' : '35px'};
  width: ${(props) =>
    props.type === 'checkbox' || props.type === 'radio' ? '17px' : 'unset'};
  padding: ${(props) =>
    props.type === 'checkbox' || props.type === 'radio' ? '0' : '0 12px'};
  box-shadow: ${(props) =>
    props.type !== 'checkbox' && props.type !== 'radio'
      ? props.error
        ? `0px 0px 0px 1px ${props.theme.errorColor}`
        : `0px 0px 0px 1px ${props.theme.inputBorderColor}`
      : 'none'};
  transition: all ease-in-out 0.15s;
  opacity: ${(props) => (props.disabled ? 0.35 : 1)};
  ${space}
  ${layout}
  ${flexbox}

  ::placeholder {
    color: ${(props) => props.theme.inputPlaceholderColor};
  }

  &[data-focus-visible-added] {
    border-color: transparent;
    outline: none;
    box-shadow: ${(props) =>
      props.error
        ? `0px 0px 0px 1px ${props.theme.errorColor}, 0px 0px 0px 4px ${hexToRgb(
            props.theme.errorColor,
            { opacity: 25 }
          )}`
        : `0px 0px 0px 1px ${
            props.theme.inputBorderColorFocus
          }, 0px 0px 0px 4px ${hexToRgb(props.theme.inputBorderColorFocus, {
            opacity: 25,
          })}}`};
  }
`;

export const InputLabel = styled(Label)`
  position: absolute;
  top: ${(props) => (props.shouldMove || props.matchStripeAppearance ? '-20px' : '9px')};
  left: ${(props) =>
    props.matchStripeAppearance ? '0px' : props.shouldMove ? '4px' : '13px'};
  color: ${(props) =>
    props.shouldMove || props.matchStripeAppearance
      ? props.error
        ? props.theme.errorColor
        : props.color
        ? props.color
        : props.theme.inputLabelColor
      : props.theme.inputPlaceholderColor};
  font-family: ${(props) => props.theme.fontText};
  font-variant: ${(props) =>
    props.shouldMove && !props.matchStripeAppearance ? 'all-small-caps' : 'normal'};
  font-size: ${(props) => (props.matchStripeAppearance ? '1.3rem' : '1.4rem')};
  pointer-events: ${(props) =>
    props.shouldMove || props.matchStripeAppearance ? 'auto' : 'none'};
  transition: all 0.2s;
`;

const InputComponent = (props) => {
  return (
    <div className={props.className}>
      <StyledInput
        id={props.id ? props.id : props.name ? props.name : undefined}
        name={props.name}
        onChange={props.onChange}
        onFocus={props.onFocus}
        onBlur={props.onBlur}
        value={props.value}
        required={props.required}
        type={props.type}
        error={props.error}
        checked={props.checked}
        pattern={props.pattern}
        disabled={props.disabled}
        autoComplete={props.autocomplete}
        min={props.min}
        placeholder={
          props.placeholder && props.matchStripeAppearance ? props.placeholder : undefined
        }
      />
      {props.placeholder && (
        <InputLabel
          htmlFor={props.name}
          error={props.error}
          shouldMove={props.value ? true : false}
          matchStripeAppearance={props.matchStripeAppearance ?? false}
          color={props.placeholderColor}
        >
          {props.placeholder}
        </InputLabel>
      )}
    </div>
  );
};

export const Input = styled(InputComponent)`
  display: flex;
  flex-direction: column;
  position: relative;
  box-shadow: ${(props) =>
    (props.type === 'checkbox' || props.type === 'radio') && props.error
      ? `0px 0px 0px 1px ${props.theme.errorColor}`
      : 'none'};
  ${space}
  ${flexbox}
  ${layout}
  ${borderRadius}

  @media screen and (max-width:767px) {
    flex: ${(props) =>
      props.type === 'checkbox' || props.type === 'radio'
        ? ''
        : '0 0 calc(100% - 25px);'};
  }
`;

const UnStyledCard = ({ className, onChange, isLoading }) => {
  return (
    <CardElement
      onChange={onChange}
      className={className}
      options={{
        hidePostalCode: true,
        disabled: isLoading,
        style: {
          base: {
            fontFamily: 'Faro-Regular, sans-serif',
            fontSize: '14px',
            color: '#44525F',
            iconColor: '#44525F',
            '::placeholder': {
              color: '#b6bbc4',
            },
          },
          invalid: {
            color: '#e55457',
            iconColor: '#e55457',
          },
          empty: {
            iconColor: '#b6bbc4',
          },
        },
      }}
    />
  );
};

export const Card = styled(UnStyledCard)`
  background-color: ${(props) => props.theme.inputBackgroundColor};
  border: 1px solid
    ${(props) => (props.error ? 'transparent' : props.theme.inputBorderColor)};
  border-radius: 4px;
  padding: 8px 12px;
  margin-bottom: ${(props) => (props.isMobile ? '16px' : '32px')};
  height: 35px;
  opacity: ${(props) => (props.isLoading ? 0.35 : 1)};

  &.StripeElement--focus {
    border-color: transparent;
    outline: none;
    box-shadow: ${(props) =>
      `0px 0px 0px 1px ${props.theme.inputBorderColorFocus}, 0px 0px 0px 4px ${hexToRgb(
        props.theme.inputBorderColorFocus,
        { opacity: 25 }
      )}}`};
  }

  &.StripeElement--focus.StripeElement--invalid {
    border-color: transparent;
    outline: none;
    box-shadow: ${(props) =>
      `0px 0px 0px 1px ${props.theme.errorColor}, 0px 0px 0px 4px ${hexToRgb(
        props.theme.errorColor,
        { opacity: 25 }
      )}}`};
  }
`;

export const Select = styled.select`
  font-family: ${(props) => props.theme.fontInput};
  font-size: 1.4rem;
  color: ${(props) => props.theme.textColor};
  background-color: #fff;
  border: none;
  appearance: none;
  ${space}
  ${layout}

  &.focus-visible {
    outline: none;
    box-shadow: ${(props) =>
      `0px 0px 0px 1px ${props.theme.inputBorderColorFocus}, 0px 0px 0px 4px ${hexToRgb(
        props.theme.inputBorderColorFocus,
        { opacity: 25 }
      )}}`};
  }
`;
